import React from "react";
import AbortablePromise from "../../../../../utils/promise/AbortablePromise";
import debounce from "../../../../../utils/debounce";
import isEqual from "lodash-es/isEqual";
import PropsProxy from "../../../../layout/PropsProxy";
import {OrderMeta} from "../Types/FieldProps";
import {StateManagerProps} from "./StateManagerProps";
import {DefaultDataGridState} from "../Types/DefaultDataGridState";
import {defaultPageSize} from "../defaults";
import {RequestParams} from "../Types/RequestParams";
import {ColumnStatesType} from "../Types/ColumnStatesType";
import {ColumnStateProps} from "../Types/ColumnStateProps";
import {DataGridProps} from "../Types/DataGridProps";
import Table from "../Table/Table";
import {RefreshSettings} from "../Types/ProviderProps";
import {ColumnStateType} from "../Types/ColumnStateType";

export default class StateManager extends React.Component<StateManagerProps, DefaultDataGridState> {

    static defaultProps: object = {
        formatRows: (items: any[]) => items,
        selection: [],
        selectionMode: "single",
    }

    public state: DefaultDataGridState;
    public refresh: () => Promise<void>;
    public refreshBackground: () => Promise<void>;
    protected onSelectionChange: () => void;
    protected loadingData: AbortablePromise<ResponseData>;

    generateState(props: StateManagerProps): DefaultDataGridState {
        const state: DefaultDataGridState = {
            isLoading: true,
            editor: {rowIndex: null, columnIndex: null, lastRowIndex: null, lastColumnIndex: null},
            lastVisibleFields: this.generateVisibleFields(props),
            visibleFields: this.generateVisibleFields(props),
            columnState: {},
            data: {
                page: props.page || 1,
                items: [],
                caption: null,
                itemsperpage: props.pageSize || defaultPageSize,
                fullcount: 0,
                itemcount: 0,
                columns: {},
                aggregations: [],
                groupAggregations: [],
                navigation: {
                    currpage: 1,
                    pagecount: 1,
                    rangeend: 1,
                    rangestart: 0,
                    link: null,
                },
            }
        };
        state.columnState = {};
        this.getFieldsByProps(props, state).map((child) => {
            if (child.props.order) {
                state.columnState[child.props.field] = {
                    order: {
                        field: child.props.field,
                        direction: (child.props.order === "DESC" || child.props.order === 1) ? "DESC" : "ASC",
                    }
                };
            }
        });
        state.request = props.generateRequest(props, state);
        /*let defaultOrders: OrderType[] = [];
        if (props.order) {
            defaultOrders = list(props.order);
        }
        defaultOrders.map((order) => {
            if (order.field) {
                state.columnState[order.field] = {order};
            }
        });*/
        return state;
    }

    generateVisibleFields(props: StateManagerProps): string[] {
        return props.visibleFields || props.getFields().map((field) => field.props.field);
    }

    setVisibleFields(fields: string[]) {
        if (fields.length > 0) {
            this.setPersistenceState({visibleFields: fields});
        }
    }

    getFieldsByProps(props: StateManagerProps, state: DefaultDataGridState, {onlyVisible = false}:{onlyVisible?: boolean} = {}) {
        return props.getFields().map((child) => {
            if (!onlyVisible || (onlyVisible && state.visibleFields.find((field) => field === child.props.field))) {
                return child;
            }
        }).filter(Boolean);
    }

    getFields({onlyVisible = false}:{onlyVisible?: boolean} = {}) {
        return this.getFieldsByProps(this.props, this.state, {onlyVisible});
    }

    loadData(request: RequestParams, {silent = false} = {}): AbortablePromise<ResponseData> {
        this.loadingData?.abort();
        this.loadingData = new AbortablePromise<ResponseData>((resolve, reject, signal) => {
            try {
                if (!silent) {
                    this.setPersistenceState({isLoading: true});
                }
                const req = this.props.loadData(request);
                signal.onAbort = () => {
                    if (!silent) {
                        this.setPersistenceState({isLoading: false});
                    }
                    req.abort();
                }
                req.then((response: ResponseData) => {
                    const Type: DataGridProps['type'] = (this.props.type || Table) as DataGridProps['type'];
                    let formatted = (Type.formatRows || ((rows: any[]) => rows))(JSON.parse(JSON.stringify(response.items)));
                    response.items = this.props.formatRows(formatted, response.items);
                    let state: DefaultDataGridState = {data: response, request};
                    if (!silent) {
                        state.isLoading = false;
                    }
                    this.setPersistenceState(state, () => {
                        resolve(response);
                    });
                })
            } catch (e) {
                reject(e);
            }
        });
        return this.loadingData;
    }

    protected getSelection(props: StateManagerProps = null): any[] {
        props = props || this.props;
        let selection = [];
        if (props.selection) {
            if (props.selection instanceof Function) {
                selection = props.selection(props);
            } else if (Array.isArray(props.selection)) {
                selection = props.selection;
            } else {
                selection = [props.selection];
            }
        }
        return selection;
    }

    protected setColumnState: (props: ColumnStateProps) => void;

    constructor(props: StateManagerProps) {
        super(props);

        this.generateState = this.generateState.bind(this);
        this.generateVisibleFields = this.generateVisibleFields.bind(this);
        this.setVisibleFields = this.setVisibleFields.bind(this);
        this.getFields = this.getFields.bind(this);
        this.loadData = this.loadData.bind(this);
        this.refresh = debounce(function (settings?: RefreshSettings) {
            return this.loadData(this.state.request, settings);
        }.bind(this), 300).bind(this);
        this.refreshBackground = this.refresh;
        this.changeOrder = this.changeOrder.bind(this);
        this.setPersistenceState = this.setPersistenceState.bind(this);
        this.setEditor = this.setEditor.bind(this);
        this.processAction = this.processAction.bind(this);
        this.getSelection = this.getSelection.bind(this);
        this.onSelectionChange = debounce(function ({item, selectedIdx, rowIdx}) {
            let selection = this.getSelection();
            let selectable = this.props.selectorFunction({item, items: this.state.data.items, selection, props: this.props, selectionType: "selectable"});
            if (selectable) {
                if (selectedIdx !== -1) {
                    selection.splice(selectedIdx, 1);
                } else {
                    selection.push(item);
                }
                if (this.props.selectionMode == "single") {
                    selection = selection[selection.length - 1] || null;
                }
                if (this.props.onSelectionChange) {
                    this.props.onSelectionChange({value: selection, props: this.props});
                }
            }
        }.bind(this), 300).bind(this);

        this.state = this.generateState(this.props);
        this.loadData(this.state.request);
        this.setColumnState = debounce(function ({field, state, meta, cb}: ColumnStateProps = {}) {
            let columnState: ColumnStatesType = {...this.state.columnState, [field]: {...(this.state.columnState[field] || {}), ...state}};
            columnState = this.changeOrder(columnState, field, state, meta);
            this.setState({columnState}, cb);
        }.bind(this), 300).bind(this);
    }

    protected mounted: boolean = false;

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    changeOrder(columnState: ColumnStatesType, field: string, state: ColumnStateType, meta: OrderMeta) {
        if (meta?.exclusive ?? false) {
            return Object.entries(columnState).map((entry) => {
                if (entry[0] !== field) {
                    delete entry[1].order;
                }
                return entry;
            }).reduce((all, entry) => ({...all, [entry[0]]: entry[1]}),{});
        }
        return columnState;
    }

    componentDidUpdate(prevProps: Readonly<StateManagerProps>, prevState: Readonly<DefaultDataGridState>, snapshot?: any) {
        const currentVisibleFields = this.generateVisibleFields(this.props);
        if (JSON.stringify(currentVisibleFields) !== JSON.stringify(this.state.lastVisibleFields)) {
            this.setPersistenceState({visibleFields: [...currentVisibleFields], lastVisibleFields: [...currentVisibleFields]});
        }

        const currentRequest = this.props.generateRequest(this.props, this.state);
        const oldRequest = this.props.generateRequest(prevProps, prevState);
        let selection: any[] = [];
        let prevSelection: any[] = [];
        if (!isEqual(this.props.selection, prevProps.selection)) {
            selection = this.getSelection(this.props);
            selection = selection.map((item) => {
                return this.props.selectorFunction({item, items: selection, selection, props: this.props, selectionType: "id"});
            });
            prevSelection = this.getSelection(prevProps);
            prevSelection = prevSelection.map((item) => {
                return prevProps.selectorFunction({item, items: prevSelection, selection: prevSelection, props: prevProps, selectionType: "id"});
            });
        }
        if (
            JSON.stringify(currentRequest) !== JSON.stringify(oldRequest)
            ||
            !isEqual(this.props.value, prevProps.value)
        ) {
            this.loadData(currentRequest);
        } else if (!isEqual(selection, prevSelection)) {
            this.loadData(this.state.request);
        }
    }

    setPersistenceState(state: DefaultDataGridState, cb?: () => void): void {
        if (this.mounted) {
            this.setState(state, cb);
        } else {
            this.state = {...this.state, ...state};
            cb && cb();
        }
    }

    setEditor(editor: {rowIndex: null|number, columnIndex: null|number}) {
        this.setPersistenceState({editor: {...editor, lastRowIndex: this.state.editor.lastRowIndex, lastColumnIndex: this.state.editor.lastColumnIndex}});
    }

    processAction(cb: Function) {
        return new AbortablePromise((resolve, reject, signal) => {
            this.setState({isLoading: true}, async () => {
                let res: any;
                try {
                    res = await cb();
                } catch (e) {
                    reject(e);
                }
                this.setState({isLoading: false}, () => {
                    resolve(res);
                });
            })
        })
    }

    render() {
        return (
            <PropsProxy
                {...this.props}
                onSelectionChange={this.props.onSelectionChange && this.onSelectionChange}
                processAction={this.processAction}
                loadData={this.loadData}
                request={this.state.request}
                setVisibleFields={this.setVisibleFields}
                visibleFields={this.state.visibleFields}
                data={this.state.data}
                columnState={this.state.columnState}
                setColumnState={this.setColumnState}
                disabled={this.props.disabled}
                isLoading={this.state.isLoading || this.props.isLoading}
                setEditor={this.setEditor}
                editor={this.state.editor}
                getFields={this.getFields}
                selection={this.getSelection()}
                selectorFunction={this.props.selectorFunction}
            >
                {this.props.children}
            </PropsProxy>
        );
    }
}